'use client';

import { useSelectedLayoutSegment } from 'next/navigation';

import { ContentTabLink } from '@ps/features/bio/content-tabs/components/ContentTabLink';
import { ContentTabStripView } from '@ps/features/bio/content-tabs/components/ContentTabStripView';
import { defaultContentTab } from '@ps/features/bio/content-tabs/functions/defaultContentTab';
import type { ContentTabStripStyle } from '@ps/features/bio/content-tabs/functions/view-models/contentTabStripStyle';
import type {
  ContentTabKind,
  ContentTabViewModel,
} from '@ps/features/bio/content-tabs/types/ContentTabViewModel';

interface Props {
  /** Visitor tab view models (hidden tabs already filtered out, server-built). */
  tabs: ContentTabViewModel[];
  /** Resolved strip colors + font (server-built). */
  strip: ContentTabStripStyle;
}

/**
 * Thin client wrapper around the pure {@link ContentTabStripView} whose only job
 * is to mark the active tab. The active tab is the selected child-route segment
 * (`useSelectedLayoutSegment()` — `store` / `life` / `feed`); at the bare index
 * `/<url_code>` no segment is selected, so it falls back to the creator's
 * {@link defaultContentTab} — the SAME default the index `page.tsx` renders, so
 * the highlight always matches the shown body.
 */
export function ContentTabStrip({ tabs, strip }: Props) {
  const segment = useSelectedLayoutSegment();
  const active: ContentTabKind | undefined =
    (segment as ContentTabKind | null) ?? defaultContentTab(tabs)?.kind;

  return (
    <ContentTabStripView stripBgColor={strip.stripBgColor}>
      {tabs.map(tab => (
        <ContentTabLink
          key={tab.kind}
          tab={tab}
          strip={strip}
          isActive={tab.kind === active}
        />
      ))}
    </ContentTabStripView>
  );
}
